import React from "react";
import {
  Box,
  Typography,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import { CircleProgressIcon } from "@rentablo/components";
import { InfoChips } from "@components/InfoChips/InfoChips";
import { toRoundedNumber } from "@helpers";
import { useRecommendationTags } from "./hooks/useRecommendationTags";
import { getRecommendationType } from "./utils";
import { RecommendationLogo } from "./common";

const RecommendationRow = styled(Box)<{ selected: boolean }>(
  ({ theme, selected }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(selected ? 2 : 1.5, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: selected
      ? theme.palette.background.neutral
      : "transparent",
    boxShadow: selected ? "0px 1px 2px 0px #1E273229" : "none",
    borderBottom: `1px solid ${theme.palette.border.secondary}`,
    gap: theme.spacing(2),
    transition: "background-color 0.3s",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
      padding: theme.spacing(2, 1),
      marginBottom: 0,
    },
  })
);

const Wrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

interface RecommenderModalRowProps {
  recommendation: FundRecommendationResponse;
  images: Record<string, string | null | undefined>;
  onSelect: () => void;
  isSelected: boolean;
  index: number;
}

export const RecommenderModalRow = ({
  recommendation,
  images,
  onSelect,
  isSelected,
  index,
}: RecommenderModalRowProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  const { tags } = useRecommendationTags(recommendation);
  const fund = recommendation.fund;
  const percentage = (fund.similarity_score ?? 0) * 100;
  const color = theme.palette.graphVisual[`0${6 + index}`]?.mid;

  const handleClick = () => {
    onSelect();
  };

  return (
    <RecommendationRow selected={isSelected}>
      <Wrapper>
        <Checkbox
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{ padding: 0, marginRight: theme.spacing(1) }}
        />

        <CircleProgressIcon
          tooltipText={t("recommender.scoreTooltip")}
          percentage={percentage}
          color="primary"
          size={35}
          icon={
            <Typography variant="overline2" color="textPrimary">
              {toRoundedNumber(percentage, 0)}%
            </Typography>
          }
        />

        <RecommendationLogo
          imageUrl={images[fund.isin]}
          alt={fund.name}
          size={isSelected ? 32 : 24}
          circleColor={isSelected ? color : undefined}
        />

        <Box>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              maxWidth: isDownSM ? "100%" : 200,
              minWidth: isDownSM ? "unset" : 200,
              mr: 5,
              whiteSpace: isDownSM ? "normal" : "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fund.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {getRecommendationType(fund.fund_type)}
          </Typography>
        </Box>
      </Wrapper>
      <InfoChips options={tags} size={isDownSM ? "xsmall" : "small"} />
    </RecommendationRow>
  );
};
